import React from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import StatusBadge from "./StatusBadge";
import ProgressBar from "./ProgressBar";
import { tokens, sharedStyles } from "../styles";

// Locality (sub-district) card — derived from data.DC entries.
// Surfaces the same KPIs as cp-routing.vue's LOCALITY tab but in the
// prototype's card hierarchy.

const safeNum = (v) =>
  v == null || Number.isNaN(Number(v)) ? null : Number(v);

const ofdPct = (card) => {
  const ofd = safeNum(card?.ofd_tps);
  const total = safeNum(card?.tp_count);
  if (ofd == null || total == null || total <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((ofd / total) * 100)));
};

const packedPct = (card) => {
  const packed = safeNum(card?.packed_tps);
  const total = safeNum(card?.tp_count);
  if (packed == null || total == null || total <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((packed / total) * 100)));
};

// Returns every DB that subscribes to this locality via cp_db_routes_v2
// (i.e. any DB with a route whose route_name matches the locality string),
// alongside the count of customers currently assigned per DB. Customer-
// derived collection (the older approach) under-reported when a locality
// was wired to multiple DBs but customers were only assigned to one of
// them — only DBs with at least one ASSIGNED tp would show up. Sourcing
// from cp_dbs[*].routes makes the chip list reflect subscription state.
const collectSubscribedDbs = (cpDbs, routeName, tpList) => {
  if (!routeName) return [];
  const tpCountByDb = new Map();
  (tpList || []).forEach((tp) => {
    const dbId = String(tp?.cp_db_id || "");
    if (!dbId || dbId === "MISROUTE" || dbId === "UNASSIGN") return;
    if (tp?.status && tp.status !== "ASSIGNED") return;
    tpCountByDb.set(dbId, (tpCountByDb.get(dbId) || 0) + 1);
  });
  const out = [];
  (cpDbs || []).forEach((db) => {
    const dbId = String(db?.cp_db_id || "");
    if (!dbId) return;
    const subscribed = (Array.isArray(db?.routes) ? db.routes : []).some(
      (r) => String(r?.route_name) === String(routeName),
    );
    if (!subscribed) return;
    out.push({
      cp_db_id: db.cp_db_id,
      db_name: db.db_name || `DB ${db.cp_db_id}`,
      count: tpCountByDb.get(dbId) || 0,
    });
  });
  return out;
};

const LocalityCard = ({ card, onPressViewMap, cpDbs }) => {
  const ofd = ofdPct(card);
  const packed = packedPct(card);
  const rescheduled =
    card?.rescheduled_crates != null ? Number(card.rescheduled_crates) : null;
  const crateCount =
    card?.crate_count != null ? Number(card.crate_count) : null;
  const newCrates =
    crateCount != null && rescheduled != null
      ? Math.max(0, crateCount - rescheduled)
      : null;
  const hasMarkers =
    Array.isArray(card?.tp_markers) && card.tp_markers.length > 0;
  const assignedDbs = collectSubscribedDbs(cpDbs, card?.title, card?.tp_list);

  return (
    <View style={sharedStyles.card}>
      <View style={styles.titleRow}>
        <Text variant="title_16" style={styles.title} numberOfLines={1}>
          {card?.title || "—"}
        </Text>
        {hasMarkers && onPressViewMap ? (
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => onPressViewMap(card.tp_markers)}
            style={styles.mapBtn}
          >
            <Text variant="caption_2" style={styles.mapBtnText}>
              View Map
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={styles.statsRow}>
        <View style={styles.stat}>
          <Text variant="caption_2" style={styles.statLabel}>
            OFD
          </Text>
          <Text variant="title_16" style={styles.statValue}>
            {ofd != null ? `${ofd}%` : "—"}
          </Text>
        </View>
        <View style={styles.stat}>
          <Text variant="caption_2" style={styles.statLabel}>
            Packed
          </Text>
          <Text variant="title_16" style={styles.statValue}>
            {packed != null ? `${packed}%` : "—"}
          </Text>
        </View>
        <View style={styles.stat}>
          <Text variant="caption_2" style={styles.statLabel}>
            TPs
          </Text>
          <Text variant="title_16" style={styles.statValue}>
            {card?.tp_count ?? 0}
          </Text>
        </View>
      </View>
      {packed != null ? (
        <View style={styles.progressWrap}>
          <ProgressBar percent={packed} height={6} />
        </View>
      ) : null}
      <View style={styles.pillsRow}>
        {crateCount != null ? (
          <StatusBadge label={`${crateCount} crates`} state="neutral" />
        ) : null}
        {newCrates != null && newCrates > 0 ? (
          <StatusBadge label={`${newCrates} new`} state="new" />
        ) : null}
        {rescheduled != null && rescheduled > 0 ? (
          <StatusBadge
            label={`${rescheduled} rescheduled`}
            state="rescheduled"
          />
        ) : null}
        {card?.unassigned_tp_count != null && card.unassigned_tp_count > 0 ? (
          <StatusBadge
            label={`${card.unassigned_tp_count} unassigned`}
            state="unpacked"
          />
        ) : null}
      </View>
      {assignedDbs.length > 0 ? (
        <View style={styles.dbsRow}>
          <Text variant="caption_2" style={styles.dbsLabel}>
            Assigned to
          </Text>
          <View style={styles.dbsChipsRow}>
            {assignedDbs.map((d) => (
              <StatusBadge
                key={String(d.cp_db_id)}
                label={`${d.db_name} · ${d.count}`}
                state="preferred"
              />
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
};

export default LocalityCard;

const styles = StyleSheet.create({
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacing.medium,
  },
  title: {
    color: tokens.textPrimary,
    fontWeight: "700",
    flex: 1,
    marginRight: tokens.spacing.small,
  },
  mapBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
  },
  mapBtnText: { color: tokens.textPrimary, fontSize: 12, fontWeight: "600" },
  statsRow: { flexDirection: "row" },
  stat: { flex: 1 },
  statLabel: { color: tokens.textSecondary, fontSize: 11 },
  statValue: {
    color: tokens.textPrimary,
    fontWeight: "700",
    fontSize: 16,
    marginTop: 2,
  },
  progressWrap: { marginTop: tokens.spacing.medium },
  pillsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: tokens.spacing.medium,
  },
  dbsRow: { marginTop: tokens.spacing.medium },
  dbsLabel: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: tokens.spacing.tiny,
  },
  dbsChipsRow: { flexDirection: "row", flexWrap: "wrap" },
});
